import { z } from "zod"

export enum OrderStatus {
  // Default status
  DRAFT = "DRAFT",
  // should auto enter this state when email is entered for the first time
  LEAD = "LEAD",
  // should enter this state when opening checkout page
  VISIT_CHECKOUT = "VISIT_CHECKOUT",
  // should enter this state when starting payment
  INITIATE_CHECKOUT = "INITIATE_CHECKOUT",
  // should enter this state on successful payment
  PURCHASE = "PURCHASE",
  // should enter this state on failed payment
  FAILED_CHECKOUT = "FAILED_CHECKOUT",
  // should enter this state when choosing multiple offers
  PLAN_SELECTED = "PLAN_SELECTED",
  // Should enter this state when cc info added
  SUBMIT_CC_INPUT = "SUBMIT_CC_INPUT",
}

export enum OrderActivityType {
  FB_PURCHASE_EVENT_SENT = "FB_PURCHASE_EVENT_SENT",
  FB_PURCHASE_BACKUP_EVENT_SENT = "FB_PURCHASE_BACKUP_EVENT_SENT",
  FB_APPEND_ATTRIBUTION_EVENT_SENT = "FB_APPEND_ATTRIBUTION_EVENT_SENT",
  FB_APPEND_ATTRIBUTION_BACKUP_EVENT_SENT = "FB_APPEND_ATTRIBUTION_BACKUP_EVENT_SENT",
  FB_SUBSCRIBE_EVENT_SENT = "FB_SUBSCRIBE_EVENT_SENT",
  FB_SUBSCRIBE_BACKUP_EVENT_SENT = "FB_SUBSCRIBE_BACKUP_EVENT_SENT",
  GA_PURCHASE_EVENT_SENT = "GA_PURCHASE_EVENT_SENT",
  NB_PURCHASE_EVENT_SENT = "NB_PURCHASE_EVENT_SENT",
  KLAVIYO_IDENTIFY_EVENT_SENT = "KLAVIYO_IDENTIFY_EVENT_SENT",
  KLAVIYO_SUBSCRIPTION_UPDATE_SENT = "KLAVIYO_SUBSCRIPTION_UPDATE_SENT",
  KLAVIYO_LEAD_EVENT_SENT = "KLAVIYO_LEAD_EVENT_SENT",
  KLAVIYO_FAILED_PAYMENT_EVENT_SENT = "KLAVIYO_FAILED_PAYMENT_EVENT_SENT",
  PINTEREST_PURCHASE_EVENT_SENT = "PINTEREST_PURCHASE_EVENT_SENT",
  SNAPCHAT_PURCHASE_EVENT_SENT = "SNAPCHAT_PURCHASE_EVENT_SENT",
  ZEROBOUNCE_EMAIL_VERIFICATION = "ZEROBOUNCE_EMAIL_VERIFICATION",
  TIK_TOK_PURCHASE_EVENT_SENT = "TIK_TOK_PURCHASE_EVENT_SENT",
}

export enum Gender {
  FEMALE = "female",
  MALE = "male",
}

export enum MeasurementSystem {
  METRIC = "metric",
  IMPERIAL = "imperial",
}

export enum TargetSystem {
  API_DIET = "api-diet",
  NNP_2 = "nnp-2",
}

export enum BodyPart {
  STOMACH = "Stomach",
  CHEST = "Chest",
  LOWER_LEGS = "Lower legs",
  THIGHS = "Thighs",
  BUTTOCKS = "Buttocks",
  SHOULDERS_AND_ARMS = "Shoulders and arms",
}

const domain = z.string()
const goal = z.enum([
  "Lose weight",
  "Get leaner & lose extra pounds",
  "Gain muscle",
  "Develop healthy habits",
])

const bodyParts = z.nativeEnum(BodyPart).array()
const gender = z.nativeEnum(Gender)
const age = z.number().int()
const phone = z.string()
const email = z
  .string()
  .email()
  .refine((val) => {
    for (let host of ["@nutrameg.com", "@science22.com", "@mitigate.dev"]) {
      if (val.includes(host)) {
        return true
      }
    }
    return !val.includes("+")
  }, "Invalid email")
const firstName = z.string()
const failedPayment = z.boolean()
const exitIntent = z.boolean()
const paymentMethod = z.string()
const url = z.string()
const metricSystem = z.literal(MeasurementSystem.METRIC)
const heightCm = z.number().int()
const weightKg = z.number().int()
const imperialSystem = z.literal(MeasurementSystem.IMPERIAL)
const heightFt = z.number().int()
const heightIn = z.number().int()
const weightLbs = z.number().int()

// Order data validations in payment state
export const OrderData = z
  .object({
    cookiesAgreed: z.boolean().nullish(),
    marketingAgreed: z.boolean().nullish(),
    domain: domain.nullish(),
    gender: gender.nullish(),
    goal: goal.nullish(),
    bodyParts: bodyParts.nullish(),
    age: age.nullish(),
    phone: phone.nullish(),
    email,
    firstName: firstName.nullish(),
    exitIntent: exitIntent.nullish(),
    failedPayment: failedPayment.nullish(),
    paymentMethod,
    paymentSuccessUrl: url,
    paymentFailUrl: url,
    utm_campaign: z.string().nullish(),
    utm_source: z.string().nullish(),
    utm_medium: z.string().nullish(),
    utm_term: z.string().nullish(),
    utm_content: z.string().nullish(),
    _fbp: z.string().nullish(),
    _fbc: z.string().nullish(),
    _ga: z.string().nullish(),
    _epik: z.string().nullish(),
    ScCid: z.string().nullish(),
    newsbreak_cid: z.string().nullish(),
    nb_cid: z.string().nullish(),
    nid: z.string().nullish(),
    token: z.string().nullish(),
    fb_ads_id: z.string().nullish(),
    ajs_anonymous_id: z.string().nullish(),
  })
  .passthrough()
  .and(
    z.union([
      z.object({
        measurementSystem: metricSystem.nullish(),
        heightCm: heightCm.nullish(),
        weightKg: weightKg.nullish(),
        targetWeightKg: weightKg.nullish(),
      }),
      z.object({
        measurementSystem: imperialSystem.nullish(),
        heightFt: heightFt.nullish(),
        heightIn: heightIn.nullish(),
        weightLbs: weightLbs.nullish(),
        targetWeightLbs: weightLbs.nullish(),
      }),
    ])
  )

// Order data validations in draft state
export const PartialOrderData = z
  .object({
    cookiesAgreed: z.boolean().nullish(),
    marketingAgreed: z.boolean().nullish(),
    domain: domain.nullish(),
    gender: gender.nullish(),
    goal: goal.nullish(),
    bodyParts: bodyParts.nullish(),
    age: age.nullish(),
    phone: phone.nullish(),
    email: email.nullish(),
    firstName: firstName.nullish(),
    exitIntent: exitIntent.nullish(),
    failedPayment: failedPayment.nullish(),
    paymentMethod: paymentMethod.nullish(),
    paymentSuccessUrl: url.nullish(),
    paymentFailUrl: url.nullish(),
    utm_campaign: z.string().nullish(),
    utm_source: z.string().nullish(),
    utm_medium: z.string().nullish(),
    utm_term: z.string().nullish(),
    utm_content: z.string().nullish(),
    _fbp: z.string().nullish(),
    _fbc: z.string().nullish(),
    _ga: z.string().nullish(),
    ajs_anonymous_id: z.string().nullish(),
    ScCid: z.string().nullish(),
    newsbreak_cid: z.string().nullish(),
    nb_cid: z.string().nullish(),
    _epik: z.string().nullish(),
  })
  .passthrough()
  .and(
    z.union([
      z.object({
        measurementSystem: metricSystem.nullish(),
        heightCm: heightCm.nullish(),
        weightKg: weightKg.nullish(),
        targetWeightKg: weightKg.nullish(),
      }),
      z.object({
        measurementSystem: imperialSystem.nullish(),
        heightFt: heightFt.nullish(),
        heightIn: heightIn.nullish(),
        weightLbs: weightLbs.nullish(),
        targetWeightLbs: weightLbs.nullish(),
      }),
    ])
  )

export class InvalidEmailDomainError extends Error {}
export class EmailVerificationError extends Error {}
